import { legacyLocationType } from "@shared/location-types";
import { hasPermission, type RoleDefinition, type User } from "@/lib/auth";
import { ALL_PERMISSIONS, OLD_TO_NEW_PERMISSION_MAP } from "../../../../shared/permissions";
import { applyPersonalPermissions } from "../../../../shared/people-access";
import { isProtectedAdministratorRole } from "../../../../shared/roleProtection";

export interface AccessPerson extends User {
  canInitiateTransfers?: boolean;
  active?: boolean;
  permissionOverrides?:{permissionId:string;effect:string}[];
  hiddenSensitiveGroups?:string[];
  tidyFlags?:string[];
}
export interface AccessLocation { id: string; name: string; nameAr?: string; type: string }
export interface PersonPatch {
  roleId: string | null;
  managedLocationIds: string[];
  requestLocationIds: string[];
  deliveryLocationIds: string[];
  canInitiateTransfers: boolean;
  canViewPrices: boolean | null;
}
export interface SensitiveGroup { id: string; label: string; pages: string[]; permissions: string[]; note?: string }
export const SENSITIVE_GROUPS: SensitiveGroup[] = [
  { id:"costs", label:"Costs & prices", pages:["Cost reports","Item prices","Waste costs"], permissions:["reports.cost_view","reports.cost_view_all","waste.view_costs"], note:"Price overrides apply to price displays; report and waste-cost grants are independent." },
  { id:"suppliers", label:"Supplier balances", pages:["Suppliers · Payments & balances"], permissions:["suppliers.payments.view"], note:"Branch and supplier-wide scope checks still apply." },
  { id:"boxes", label:"Cash boxes", pages:["Branch cash · Cash boxes"], permissions:["branch_cash.view_boxes"], note:"Cash PINs and record/manage authority remain separate." },
  { id:"book", label:"Cash book", pages:["Branch cash · Cash book"], permissions:["branch_cash.view_book"] },
  { id:"held", label:"Held money", pages:["Branch cash · Personal floats"], permissions:["branch_cash.view_held"], note:"This describes management visibility. People can also see their own held-money records through existing personal access." },
  { id:"pay", label:"Pay", pages:["Staff · Pay","Staff · Pay records"], permissions:["staff.pay.view"], note:"Existing Staff location, Finance and action restrictions still apply; this does not grant payroll actions." },
  { id:"personal", label:"Personal details", pages:["Staff · Employee details","Maintenance · People who fix","People"], permissions:["employees.view","maintenance.view_fixer_personal","users.manage"], note:"Yes means access to at least one listed source, not every personal field. National IDs, ID photos and proofs have additional controls." },
  { id:"maintenance", label:"Maintenance costs", pages:["Maintenance · Spending","Maintenance · Jobs"], permissions:["maintenance.view_spend"], note:"Requires both Maintenance access and its explicit spend grant, including for system roles." },
  { id:"sales", label:"Sales", pages:["Sales uploads","Consumption report"], permissions:["sales.upload","reports.consumption.view"] },
];

export const canOpenPeopleAccess = (person: User | null, roles: RoleDefinition[]) =>
  ["users.manage","roles.manage"].some(key => hasPermission(person,key,roles));

export function roleBypass(role?: RoleDefinition): boolean {
  return isProtectedAdministratorRole(role);
}
export function seesPrices(person: AccessPerson, roles: RoleDefinition[]): boolean {
  if(person.hiddenSensitiveGroups?.includes("costs"))return false;
  return person.canViewPrices ?? hasPersonPermission(person,"view_prices",roles);
}
const personalRoleCache=new WeakMap<AccessPerson,{roles:RoleDefinition[];projected:RoleDefinition[]}>();
function hasPersonPermission(person:AccessPerson,key:string,roles:RoleDefinition[]):boolean {
  if(!person.permissionOverrides?.length)return hasPermission(person,key,roles);
  const cached=personalRoleCache.get(person);
  if(cached?.roles===roles)return hasPermission(person,key,cached.projected);
  const role=roles.find(item=>item.id===person.roleId);
  if(!role)return false;
  const base=[...new Set([...ALL_PERMISSIONS.filter(p=>hasPermission(person,p.key,roles)).map(p=>p.key),...role.permissions.map(p=>p.id)])];
  const keys=applyPersonalPermissions(base,person.permissionOverrides);
  const projected={...role,isSystem:false,permissions:keys.map(id=>role.permissions.find(p=>p.id===id)||{id,name:id,description:null,category:"custom"})};
  const projectedRoles=roles.map(item=>item.id===role.id?projected:item);
  personalRoleCache.set(person,{roles,projected:projectedRoles});
  return hasPermission(person,key,projectedRoles);
}
export function canSeeSensitive(person: AccessPerson, group: SensitiveGroup, roles: RoleDefinition[]): boolean {
  if(person.hiddenSensitiveGroups?.includes(group.id))return false;
  if(group.id==="maintenance") return hasPersonPermission(person,"maintenance.view",roles) && hasPersonPermission(person,"maintenance.view_spend",roles);
  if(group.id==="costs") return seesPrices(person,roles) || group.permissions.some(key=>hasPersonPermission(person,key,roles));
  return group.permissions.some(key=>hasPersonPermission(person,key,roles));
}
const normalizedName=(name:string)=>name.trim().replace(/\s+/g," ").toLocaleLowerCase();
export function tidyIssues(person: AccessPerson, people: AccessPerson[], roles: RoleDefinition[]): string[] {
  const issues:string[]=[...(person.tidyFlags||[])];
  if(!person.roleId || !roles.some(role=>role.id===person.roleId)) {
    issues.push("No role");
    if(person.managedLocationIds?.length) issues.push("Manages locations without a role");
  }
  if(people.some(other=>other.id!==person.id && normalizedName(other.name)===normalizedName(person.name) &&
    other.email.trim().toLowerCase()!==person.email.trim().toLowerCase())) issues.push("Possible duplicate login");
  return [...new Set(issues)];
}
export function tidyIssueIndex(people:AccessPerson[],roles:RoleDefinition[]) {
  const names=new Map<string,Set<string>>(),roleIds=new Set(roles.map(role=>role.id));
  for(const person of people){
    const name=normalizedName(person.name),emails=names.get(name)||new Set<string>();
    emails.add(person.email.trim().toLowerCase());names.set(name,emails);
  }
  return new Map<string,string[]>(people.map(person=>{
    const issues:string[]=[...(person.tidyFlags||[])];
    if(!person.roleId||!roleIds.has(person.roleId)){
      issues.push("No role");
      if(person.managedLocationIds?.length)issues.push("Manages locations without a role");
    }
    if((names.get(normalizedName(person.name))?.size||0)>1)issues.push("Possible duplicate login");
    return [person.id,[...new Set(issues)]];
  }));
}
export function personForRole(roleId:string):AccessPerson {
  return {id:`role:${roleId}`,name:"Role projection",email:"",roleId,workspaceIds:[],locationIds:[],managedLocationIds:[],requestLocationIds:[],deliveryLocationIds:[],canViewPrices:null};
}
export function peopleAtLocation(person: AccessPerson,id:string):boolean {
  return [person.managedLocationIds,person.requestLocationIds,person.deliveryLocationIds].some(ids=>ids?.includes(id));
}
export function locationSummary(person: AccessPerson,locations: AccessLocation[],roles:RoleDefinition[]):string {
  if(isProtectedAdministratorRole({ id: person.roleId || undefined })) return "All locations";
  if(hasPersonPermission(person,"people_access.all_locations",roles)) return "All locations";
  const ids=person.managedLocationIds||[];
  if(!ids.length) return "No managed locations";
  // /locations can itself be scoped. Seeing every returned row is not evidence
  // that this person has access to the whole workspace.
  const allBranches=ids.every(id=>legacyLocationType(locations.find(location=>location.id===id)?.type)==="branch");
  return `${ids.length} ${allBranches ? (ids.length===1?"branch":"branches") : (ids.length===1?"location":"locations")}`;
}
export const roleColor=(id:string|null|undefined):string=>{
  const colors=["bg-blue-50 text-blue-700","bg-violet-50 text-violet-700","bg-emerald-50 text-emerald-700","bg-amber-50 text-amber-800","bg-rose-50 text-rose-700"];
  return colors[Array.from(id||"").reduce((sum,char)=>sum+char.charCodeAt(0),0)%colors.length];
};
export type PermissionAction="See"|"Record"|"Approve"|"Set up";
type PermissionGroup={area:string;permissions:{key:string;label:string;action:PermissionAction}[]};
const groupCache=new WeakMap<RoleDefinition,PermissionGroup[]>();
export function permissionAction(key:string):PermissionAction {
  if(/approve|review|signoff|sign_off|reject/.test(key)) return "Approve";
  if(/manage|settings|setup|configure|merge|policy|rules/.test(key)) return "Set up";
  if(/view|read|report|analytics|export|print|download/.test(key)) return "See";
  return "Record";
}
function areaFor(key:string,category:string):string {
  if(/^(orders|templates|catalog)\./.test(key)||category==="orders"||category==="templates")return "Ordering";
  if(/^supplier_receipts\./.test(key))return "Receiving";
  if(/^(purchase|procurement|suppliers|delivery_companies)/.test(key)||category==="procurement")return "Purchasing";
  if(/^(branch_cash|expense_setup)\./.test(key)||category==="branch_cash")return "Cash";
  if(/^maintenance\./.test(key))return "Maintenance";
  if(/^(staff|employees|uniforms)\./.test(key)||category==="uniforms"||category==="employees")return "Staff";
  if(/^(sales|recipes)\./.test(key)||category==="sales_consumption")return "Sales";
  if(/^(inventory|items|transfers|stocktake|waste|returns)\./.test(key))return "Stock";
  if(/^(reports|analytics)\./.test(key))return "Reports";
  if(/^(users|roles|locations|settings|audit|vehicles|rules)\./.test(key)||category==="admin")return "Settings";
  return category ? category.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()) : "Other";
}
export function permissionGroups(person:AccessPerson,roles:RoleDefinition[]) {
  const role=roles.find(item=>item.id===person.roleId);
  if(!role)return [];
  const cached=person.permissionOverrides?.length?undefined:groupCache.get(role);
  if(cached)return cached;
  const definitions=new Map(ALL_PERMISSIONS.map(def=>[def.key,def]));
  const candidates=new Set(ALL_PERMISSIONS.map(def=>def.key));
  for(const perm of role.permissions) {
    if(perm.id==="purchase_requests.view_all") continue; // retired read alias, not a second control
    const migrated=OLD_TO_NEW_PERMISSION_MAP[perm.id];
    if(migrated)for(const key of migrated)candidates.add(key);
    else candidates.add(perm.id);
  }
  const groups=new Map<string,{key:string;label:string;action:PermissionAction}[]>();
  for(const key of candidates){
    if(!hasPersonPermission(person,key,roles))continue;
    const def=definitions.get(key), stored=role.permissions.find(perm=>perm.id===key);
    const area=areaFor(key,def?.category||stored?.category||"");
    const permissions=groups.get(area)||[];
    const caption=def?.label||stored?.name||key;
    const label=/^[a-z0-9_.]+$/i.test(caption)&&/[._]/.test(caption)
      ?caption.replace(/[._]+/g," ").replace(/\b\w/g,letter=>letter.toUpperCase())
      :caption;
    permissions.push({key,label,action:permissionAction(key)});
    groups.set(area,permissions);
  }
  const result=Array.from(groups,([area,permissions])=>({area,permissions})).sort((a,b)=>a.area.localeCompare(b.area));
  if(!person.permissionOverrides?.length)groupCache.set(role,result);
  return result;
}
